import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookUser, AudioLines, Calculator, Map, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_LABELS = { nuovo: "Nuovo", contattato: "Contattato", trattativa: "In trattativa", prenotato: "Prenotato", in_spedizione: "In spedizione", archiviato: "Archiviato" };

export default function Dashboard() {
  const isAdmin = useAuth().user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    api.get("/admin/crm/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/crm/leads").then((r) => setLeads(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const cards = [
    { label: "Contatti totali", value: stats?.leads_total, icon: BookUser, to: "/admin/crm", id: "stat-leads" },
    { label: "Spedizioni pubblicate", value: stats?.tours_published, icon: Map, to: "/admin/viaggi", id: "stat-tours" },
    ...(isAdmin
      ? [
          { label: "Media in libreria", value: stats?.media_count, icon: AudioLines, to: "/admin/media", id: "stat-media" },
          { label: "Markup medio piani", value: stats ? `${stats.avg_markup}%` : null, icon: Calculator, to: "/admin/planning", id: "stat-markup" },
        ]
      : []),
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif text-3xl sm:text-4xl">Benvenuto alla base.</h1>
      <p className="text-cream2 text-sm mt-2">Tutto quello che serve per gestire De Los Cielos, in un solo posto.</p>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mt-8">
        {cards.map((c) => (
          <Link key={c.id} to={c.to} className="card-dark p-5 hover:border-copper/40 transition-colors group" data-testid={c.id}>
            <div className="flex items-center justify-between">
              <c.icon className="h-5 w-5 text-copper" />
              <ArrowRight className="h-4 w-4 text-sand opacity-0 group-hover:opacity-100 group-hover:text-copper transition-all" />
            </div>
            <p className="font-serif text-4xl mt-4 text-cream">{c.value ?? "—"}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-sand mt-1">{c.label}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mt-8">
        <div className="card-dark p-6 xl:col-span-2" data-testid="dashboard-recent-leads">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl">Ultimi contatti</h2>
            <Link to="/admin/crm" className="link-underline font-mono text-[10px] uppercase tracking-widest text-copperlight">Apri CRM</Link>
          </div>
          <div className="space-y-2">
            {leads.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0" data-testid="dashboard-lead-row">
                <div className="min-w-0">
                  <p className="text-sm text-cream truncate">{l.name} <span className="text-sand">· {l.email}</span></p>
                  <p className="font-mono text-[10px] text-sand mt-0.5 uppercase tracking-widest">{l.destination_interest || "—"}</p>
                </div>
                <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border border-copper/30 text-copperlight">{STATUS_LABELS[l.status] || l.status}</span>
              </div>
            ))}
            {leads.length === 0 && <p className="text-sand text-sm">Nessun contatto ancora.</p>}
          </div>
        </div>
        <div className="card-dark p-6" data-testid="dashboard-conversion">
          <h2 className="font-serif text-2xl">Conversione</h2>
          <p className="font-serif text-6xl text-copper mt-6">{stats ? `${stats.conversion}%` : "—"}</p>
          <p className="text-sm text-cream2 mt-2">Dei contatti ha prenotato o è in spedizione.</p>
          {stats && stats.top_destinations && stats.top_destinations.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand mb-3">Destinazioni più richieste</p>
              {stats.top_destinations.map(([d, n]) => (
                <div key={d} className="flex justify-between text-sm text-cream2 py-1">
                  <span className="capitalize">{d.replace(/-/g, " ")}</span><span className="font-mono text-copper">{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
